import { useEffect } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Grid from "@mui/material/Grid";

import {
  CategoryEdit,
  CategoryEditConstructor,
} from "../../models/CategoryEdit";
import { useEditCategory } from "../../hooks/useEditCategory";
import { useApiFieldErrors } from "../../../../hooks/fetch/useApiFieldErrors";
import { useRefetchOnSuccess } from "../../../../hooks/fetch/useRefetchOnSuccess";
import { useFieldRules } from "../../../../hooks/useFieldRules";

import FormCardWrapper from "../../../../components/reusable-components/form/FormCardWrapper";
import FormTextField from "../../../../components/reusable-components/form/MuiFormInput";
import SubmitButton from "../../../../components/reusable-components/form/SubmitButton";
import DomainSelectForm from "../../../domain/components/form/DomainSelectForm";
import { usePage } from "../../../pages/hooks/usePage";
import { useCategory } from "../../hooks/useCategory";
import CategorySelectForm from "../CategorySelectForm";

const EditCategoryForm = ({ id }: { id: string }) => {
  const rules = useFieldRules();

  const {
    fetch: { data: categoryData, loading, error, refetch },
  } = useCategory({ id });

  const {
    fetch: {
      fetchData: updateCategory,
      loading: updating,
      error: updateError,
      success: updateSuccess,
    },
  } = useEditCategory({ id });

  const {
    handleSubmit,
    control,
    reset,
    setError,
    clearErrors,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<CategoryEditConstructor>({
    defaultValues: {
      name: "",
      slug: "",
      domain_id: undefined,
      parent_id: null,
    },
    mode: "all",
  });

  useEffect(() => {
    if (categoryData) {
      reset({
        name: categoryData.name,
        slug: categoryData.slug,
        domain_id: {
          label: categoryData.domain?.name ?? categoryData.domain_id,
          value: categoryData.domain_id,
        },
        parent_id: categoryData.parent_id
          ? {
              label: categoryData.parent_id.toString(),
              value: categoryData.parent_id.toString(),
            }
          : null,
      });
    }
  }, [categoryData, reset]);

  useRefetchOnSuccess(updateSuccess, refetch);
  useApiFieldErrors(updateError, setError);

  const onSubmit: SubmitHandler<CategoryEditConstructor> = async (formData) => {
    clearErrors();
    await updateCategory({ body: new CategoryEdit(formData) });
  };

  const isLoading = loading || updating || isSubmitting;

  return (
    <FormCardWrapper
      title="Edit Category"
      successText="Category updated successfully!"
      success={updateSuccess}
      error={error || updateError}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="name"
            label="Name"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Name").required().maxLength(255).get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormTextField
            name="slug"
            label="Slug"
            control={control}
            textFieldProps={{ disabled: isLoading }}
            rules={rules("Slug").required().slug().get()}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <DomainSelectForm
            name="domain_id"
            control={control}
            disabled={true}
            controllerProps={{
              rules: rules("Domain").required().get(),
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <CategorySelectForm
            name="parent_id"
            control={control}
            domainId={watch("domain_id")}
            watch={watch}
            setValue={setValue}
            disabled={isLoading || !watch("domain_id")?.value}
            textFieldProps={{
              label: "Parent Category",
              placeholder: "Search parent category...",
            }}
            controllerProps={{
              rules: rules("Parent Category").required().get(),
            }}
          />
        </Grid>
      </Grid>

      <SubmitButton
        text="Update Category"
        loading={updating || isSubmitting}
        disabled={isLoading}
      />
    </FormCardWrapper>
  );
};

export default EditCategoryForm;
